import { ref, set, get, update, push, onValue, remove, runTransaction } from 'firebase/database';
import { database } from '../firebaseConfig';
import {
  Tournament,
  TournamentMatch,
  TournamentMatchResult,
  TournamentGroup,
  TournamentParticipant,
} from '../types';
import { applyMatchRating } from './ratingService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const nextPowerOf2 = (n: number): number => {
  let p = 1;
  while (p < n) p *= 2;
  return p;
};

/**
 * Standard tournament bracket seed order (1-based seed numbers), e.g. for 8
 * slots: [1,8,4,5,2,7,3,6]. Pairing consecutive entries puts seeds 1 and 2 in
 * opposite bracket halves so they can only meet in the final, and spreads byes
 * (the highest "seeds", which are empty slots) across the bracket. The previous
 * linear pairing (i vs slots-1-i) made the top two seeds collide one round
 * after the byes.
 */
export const bracketSeedOrder = (slots: number): number[] => {
  let order = [1];
  while (order.length < slots) {
    const mirrorSum = order.length * 2 + 1;
    const next: number[] = [];
    order.forEach((s) => {
      next.push(s);
      next.push(mirrorSum - s);
    });
    order = next;
  }
  return order;
};

const roundName = (round: number, totalRounds: number): string => {
  const remaining = totalRounds - round;
  if (remaining === 1) return 'Final';
  if (remaining === 2) return 'Semifinals';
  if (remaining === 3) return 'Quarterfinals';
  return `Round ${round + 1}`;
};

/** Shuffle array in-place (Fisher-Yates) */
const shuffle = <T>(arr: T[]): T[] => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/**
 * Round-0 bye matches are pre-completed but never trigger advancement (there's
 * no game to play/save). Push their winners directly into their round-1 slot so
 * the next round isn't left with empty slots that can never be filled.
 * Mutates the in-memory playoffRounds before it's written.
 */
const advanceByeWinners = (playoffRounds: { [ri: string]: any }): void => {
  const round0 = playoffRounds['0'];
  const round1 = playoffRounds['1'];
  if (!round0 || !round1) return; // a single-match final has no round 1 / no byes

  Object.values(round0.matches || {}).forEach((m: any) => {
    if (m.player2 !== null || !m.player1 || m.matchIndex == null) return; // not a bye
    const ni = Math.floor(m.matchIndex / 2);
    const slotKey = m.matchIndex % 2 === 0 ? 'player1' : 'player2';
    if (!round1.matches) round1.matches = {};
    const existing = round1.matches[`${ni}`] || {
      id: `r1_m${ni}`,
      roundIndex: 1,
      matchIndex: ni,
      status: 'pending',
      phase: 'playoff',
    };
    existing[slotKey] = { uid: m.player1.uid, name: m.player1.name };
    round1.matches[`${ni}`] = existing;
  });
};

/**
 * Where a match winner advances to. Pure — shared by the real advancement code
 * and the offline simulator so they can't diverge.
 */
export const nextBracketSlot = (
  matchIndex: number
): { nextMatchIndex: number; slotKey: 'player1' | 'player2' } => ({
  nextMatchIndex: Math.floor(matchIndex / 2),
  slotKey: matchIndex % 2 === 0 ? 'player1' : 'player2',
});

/**
 * Build the full playoffRounds structure from an ordered/seeded participant list:
 * round-0 pairings in standard bracket order, byes pre-advanced, later rounds
 * created empty. Pure — no Firebase — so it's the single source of truth shared
 * by both bracket generators and the offline simulator.
 */
export const buildPlayoffRounds = (
  seededPlayers: { uid: string; name: string }[],
  matchIdPrefix = 'r'
): { [ri: string]: any } => {
  const slots = nextPowerOf2(seededPlayers.length);
  const numRounds = Math.log2(slots);

  const seeded: ({ uid: string; name: string } | null)[] = [...seededPlayers];
  while (seeded.length < slots) seeded.push(null);

  const order = bracketSeedOrder(slots);
  const firstRoundMatches: { [mi: string]: TournamentMatch } = {};
  for (let i = 0; i < slots / 2; i++) {
    const p1 = seeded[order[2 * i] - 1] ?? null;
    const p2 = seeded[order[2 * i + 1] - 1] ?? null;
    const isBye = p2 === null;
    const match: TournamentMatch = {
      id: `${matchIdPrefix}0_m${i}`,
      roundIndex: 0,
      matchIndex: i,
      player1: p1!,
      player2: p2,
      status: isBye ? 'completed' : 'pending',
      phase: 'playoff',
    };
    // Only attach result for byes — Firebase rejects `undefined`, so omit the key
    // entirely for normal matches.
    if (isBye) {
      match.result = { winner: p1!.uid, player1Wins: 1, player2Wins: 0, points: { [p1!.uid]: 3 } };
    }
    firstRoundMatches[`${i}`] = match;
  }

  const playoffRounds: { [ri: string]: any } = {
    '0': { roundIndex: 0, name: roundName(0, numRounds), matches: firstRoundMatches },
  };
  for (let r = 1; r < numRounds; r++) {
    playoffRounds[`${r}`] = { roundIndex: r, name: roundName(r, numRounds), matches: {} };
  }

  advanceByeWinners(playoffRounds);
  return playoffRounds;
};

/** Normalize Firebase data — handles objects-stored-as-arrays and missing fields */
export const normalizeTournament = (id: string, data: any): Tournament => {
  const participants: { [uid: string]: TournamentParticipant } = {};
  if (data.participants && typeof data.participants === 'object') {
    Object.entries(data.participants).forEach(([uid, p]: [string, any]) => {
      participants[uid] = p;
    });
  }

  const groups: { [gid: string]: TournamentGroup } = {};
  if (data.groups && typeof data.groups === 'object') {
    Object.entries(data.groups).forEach(([gid, g]: [string, any]) => {
      groups[gid] = {
        ...g,
        playerIds: Array.isArray(g.playerIds)
          ? g.playerIds
          : Object.values(g.playerIds || {}),
      };
    });
  }

  const poolMatches: { [matchId: string]: TournamentMatch } = {};
  if (data.poolMatches && typeof data.poolMatches === 'object') {
    Object.entries(data.poolMatches).forEach(([mid, m]: [string, any]) => {
      poolMatches[mid] = m as TournamentMatch;
    });
  }

  const playoffRounds: { [ri: string]: any } = {};
  if (data.playoffRounds && typeof data.playoffRounds === 'object') {
    Object.entries(data.playoffRounds).forEach(([ri, r]: [string, any]) => {
      const matches: { [mi: string]: TournamentMatch } = {};
      if (r.matches && typeof r.matches === 'object') {
        Object.entries(r.matches).forEach(([mi, m]: [string, any]) => {
          // Drop rogue/partial entries (e.g. a stray `{status:'active'}` with no
          // players from an old start-match bug) — they'd crash the screens.
          // A real match always has at least one player slot.
          if (m && (m.player1 || m.player2)) {
            matches[mi] = m as TournamentMatch;
          }
        });
      }
      playoffRounds[ri] = { ...r, matches };
    });
  }

  return {
    ...data,
    id,
    participants,
    groups: Object.keys(groups).length > 0 ? groups : undefined,
    poolMatches: Object.keys(poolMatches).length > 0 ? poolMatches : undefined,
    playoffRounds: Object.keys(playoffRounds).length > 0 ? playoffRounds : undefined,
  } as Tournament;
};

// ─── CRUD ─────────────────────────────────────────────────────────────────────

export const createTournament = async (data: {
  name: string;
  format: 'pool' | 'bracket';
  bestOf: 1 | 3 | 5;
  isChampionship?: boolean;
  poolSettings?: { numberOfGroups: number; advanceCount: number };
  bracketSettings?: { seedingType: 'random' | 'manual' };
  createdBy: string;
}): Promise<string> => {
  const tournamentsRef = ref(database, 'tournaments');
  const newRef = push(tournamentsRef);
  const id = newRef.key!;

  await set(newRef, {
    id,
    name: data.name,
    format: data.format,
    bestOf: data.bestOf,
    isChampionship: data.isChampionship || false,
    poolSettings: data.poolSettings || null,
    bracketSettings: data.bracketSettings || null,
    createdBy: data.createdBy,
    createdAt: Date.now(),
    status: 'registration',
    participants: {},
    winner: null,
  });

  return id;
};

/** Update pool settings (groups count / advance count) — callable while still in draft/registration */
export const updatePoolSettings = async (
  tournamentId: string,
  settings: { numberOfGroups: number; advanceCount: number }
): Promise<void> => {
  await update(ref(database, `tournaments/${tournamentId}`), { poolSettings: settings });
};

export const deleteTournament = async (tournamentId: string): Promise<void> => {
  await remove(ref(database, `tournaments/${tournamentId}`));
};

export const getAllTournaments = async (): Promise<Tournament[]> => {
  const snapshot = await get(ref(database, 'tournaments'));
  if (!snapshot.exists()) return [];
  return Object.entries(snapshot.val()).map(([id, t]: [string, any]) =>
    normalizeTournament(id, t)
  );
};

export const subscribeAllTournaments = (
  callback: (tournaments: Tournament[]) => void
) => {
  const tournamentsRef = ref(database, 'tournaments');
  return onValue(tournamentsRef, (snapshot) => {
    if (!snapshot.exists()) { callback([]); return; }
    const tournaments = Object.entries(snapshot.val()).map(([id, t]: [string, any]) =>
      normalizeTournament(id, t)
    );
    callback(tournaments);
  });
};

export const subscribeTournament = (
  id: string,
  callback: (t: Tournament | null) => void
) => {
  const tournamentRef = ref(database, `tournaments/${id}`);
  return onValue(tournamentRef, (snapshot) => {
    if (!snapshot.exists()) { callback(null); return; }
    callback(normalizeTournament(id, snapshot.val()));
  });
};

// ─── Registration ─────────────────────────────────────────────────────────────

export const registerForTournament = async (
  tournamentId: string,
  uid: string,
  name: string
): Promise<void> => {
  await set(ref(database, `tournaments/${tournamentId}/participants/${uid}`), {
    uid,
    name,
    registeredAt: Date.now(),
    status: 'pending',
  });
};

/**
 * Is this device actually talking to the backend RIGHT NOW?
 *
 * `.info/connected` is maintained locally by the SDK and never hits the
 * network, so this resolves instantly when online. The timeout only covers the
 * case where the SDK has not established state yet — treated as "not
 * connected", i.e. the pessimistic answer.
 */
const isConnectedToBackend = (timeoutMs = 4000): Promise<boolean> =>
  new Promise((resolve) => {
    let settled = false;
    let unsub: (() => void) | null = null;
    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      if (unsub) unsub();
      resolve(value);
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    unsub = onValue(
      ref(database, '.info/connected'),
      (snap) => {
        clearTimeout(timer);
        finish(snap.val() === true);
      },
      () => {
        clearTimeout(timer);
        finish(false);
      }
    );
  });

/**
 * Has this match result actually LANDED on the server?
 *
 * Why this exists: the RTDB SDK applies a write to its local cache immediately
 * and syncs it later. On a flaky connection that means
 *   • listeners fire as though the write succeeded, and
 *   • a subsequent `get()` can be served from that same local cache.
 * The idempotency guard in completeTournamentPoolMatch reads exactly that
 * `status` — so a retry could see a locally-cached 'completed', return early
 * without writing anything, and the UI would report success for a result the
 * backend never received. Results were lost this way in a live tournament and
 * had to be awarded by hand.
 *
 * Being connected is checked FIRST: `get()` only reaches the server when the
 * client is online, and falls back to the cache when it isn't — so without the
 * connection check the read could be just as much of a lie as the write.
 */
export const confirmTournamentMatchSaved = async (
  tournamentId: string,
  phase: 'pool' | 'playoff',
  matchId: string,
  roundIndex: number | undefined,
  matchIndex: number | undefined,
  expectedWinnerUid: string
): Promise<boolean> => {
  if (!(await isConnectedToBackend())) return false;

  const path =
    phase === 'pool'
      ? `tournaments/${tournamentId}/poolMatches/${matchId}`
      : `tournaments/${tournamentId}/playoffRounds/${roundIndex}/matches/${matchIndex}`;

  try {
    const snap = await get(ref(database, path));
    const m = snap.val();
    return !!m && m.status === 'completed' && m.result?.winner === expectedWinnerUid;
  } catch {
    // Could not read it back — treat as unconfirmed so the player retries.
    return false;
  }
};

/**
 * A rejected player asking to be reconsidered.
 *
 * Deliberately an `update`, not the `set` that registerForTournament uses: a
 * whole-object write would reset registeredAt and wipe any seed/groupId the
 * admin had already set. Only the fields below change, so the slot keeps its
 * history and the admin can see this player was declined before.
 *
 * Guarded to 'rejected' so it can never be used to flip an approved player
 * back to pending, or to re-open a slot after the tournament has started.
 */
export const reapplyForTournament = async (
  tournamentId: string,
  uid: string,
  name: string
): Promise<void> => {
  const path = `tournaments/${tournamentId}/participants/${uid}`;
  const snap = await get(ref(database, path));
  const current = snap.val();
  if (!current) throw new Error('You are not registered for this tournament.');
  if (current.status !== 'rejected') {
    throw new Error('Only a declined registration can be sent again.');
  }
  await update(ref(database, path), {
    name,
    status: 'pending',
    reappliedAt: Date.now(),
    reapplyCount: (current.reapplyCount || 0) + 1,
  });
};

export const updateParticipantStatus = async (
  tournamentId: string,
  uid: string,
  status: 'approved' | 'rejected'
): Promise<void> => {
  await update(ref(database, `tournaments/${tournamentId}/participants/${uid}`), { status });
};

/**
 * Pull a removed player out of any saved group so pool matches can't be
 * generated for them. Without this, rejecting an approved player after groups
 * were saved left their uid in groups.playerIds — a guaranteed no-show wedge.
 */
export const removeParticipantFromGroups = async (
  tournamentId: string,
  uid: string,
  tournament: Tournament
): Promise<void> => {
  if (!tournament.groups) return;
  const updates: { [path: string]: any } = {};
  Object.entries(tournament.groups).forEach(([gid, group]) => {
    if (group.playerIds?.includes(uid)) {
      updates[`tournaments/${tournamentId}/groups/${gid}/playerIds`] =
        group.playerIds.filter((id) => id !== uid);
    }
  });
  // Also clear the participant's stored group assignment
  updates[`tournaments/${tournamentId}/participants/${uid}/groupId`] = null;
  await update(ref(database), updates);
};

export const updateTournamentStatus = async (
  tournamentId: string,
  status: Tournament['status']
): Promise<void> => {
  await update(ref(database, `tournaments/${tournamentId}`), { status });
};

// ─── Group Assignment (Pool format) ──────────────────────────────────────────

/**
 * groupAssignments: { [uid]: groupIndex (0-based) }
 */
export const saveGroupAssignments = async (
  tournamentId: string,
  groupAssignments: { [uid: string]: number },
  numberOfGroups: number,
  participants: { [uid: string]: TournamentParticipant }
): Promise<void> => {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const groups: { [gid: string]: TournamentGroup } = {};

  for (let i = 0; i < numberOfGroups; i++) {
    groups[`group_${i}`] = { id: `group_${i}`, name: `Group ${letters[i]}`, playerIds: [] };
  }

  Object.entries(groupAssignments).forEach(([uid, idx]) => {
    const gid = `group_${idx}`;
    if (groups[gid] && participants[uid]) {
      groups[gid].playerIds.push(uid);
    }
  });

  const updates: { [path: string]: any } = {};
  Object.entries(groupAssignments).forEach(([uid, idx]) => {
    updates[`tournaments/${tournamentId}/participants/${uid}/groupId`] = `group_${idx}`;
  });

  await set(ref(database, `tournaments/${tournamentId}/groups`), groups);
  if (Object.keys(updates).length) await update(ref(database), updates);
};

// ─── Start Tournament ─────────────────────────────────────────────────────────

/** Pool: generate all round-robin matches within groups */
/**
 * How many matches under a node already carry a recorded result.
 *
 * The three generators below each `set()` a whole subtree, which erases every
 * result inside it. They are hidden in the UI once play has started, but that
 * is one line of defence in front of "replay the entire tournament" — a stale
 * admin tab, a double tap, or a future caller is all it takes. These guards
 * make the destructive case impossible rather than merely unlikely.
 */
const countRecordedResults = (node: any): number => {
  if (!node || typeof node !== 'object') return 0;
  return Object.values(node).reduce((n: number, m: any) => {
    if (m && typeof m === 'object' && (m.status === 'completed' || m.result)) return n + 1;
    return n;
  }, 0);
};

export const startPoolTournament = async (
  tournamentId: string,
  groups: { [gid: string]: TournamentGroup },
  participants: { [uid: string]: TournamentParticipant }
): Promise<void> => {
  const existing = await get(ref(database, `tournaments/${tournamentId}/poolMatches`));
  const played = countRecordedResults(existing.val());
  if (played > 0) {
    throw new Error(
      `Refusing to regenerate pool matches: ${played} already have a recorded result and would be erased. ` +
      'Use Match Admin to fix individual matches instead.'
    );
  }

  const poolMatches: { [matchId: string]: TournamentMatch } = {};

  Object.entries(groups).forEach(([gid, group]) => {
    const pids = group.playerIds;
    for (let i = 0; i < pids.length; i++) {
      for (let j = i + 1; j < pids.length; j++) {
        const p1uid = pids[i];
        const p2uid = pids[j];
        const p1 = participants[p1uid];
        const p2 = participants[p2uid];
        if (!p1 || !p2) continue;

        const matchId = `${gid}_${p1uid}_vs_${p2uid}`;
        poolMatches[matchId] = {
          id: matchId,
          groupId: gid,
          player1: { uid: p1uid, name: p1.name },
          player2: { uid: p2uid, name: p2.name },
          status: 'pending',
          phase: 'pool',
        };
      }
    }
  });

  await set(ref(database, `tournaments/${tournamentId}/poolMatches`), poolMatches);
  await update(ref(database, `tournaments/${tournamentId}`), { status: 'active' });
};

/** Bracket: generate first round with smart bye distribution */
export const startBracketTournament = async (
  tournamentId: string,
  orderedParticipants: { uid: string; name: string }[]
): Promise<void> => {
  // Same degenerate-bracket guard as generatePoolPlayoffs: <2 entrants would
  // produce an unplayable bye-final and wedge the tournament.
  if (orderedParticipants.length < 2) {
    throw new Error(
      `A bracket needs at least 2 players — only ${orderedParticipants.length} approved.`
    );
  }
  const existingBracket = await get(ref(database, `tournaments/${tournamentId}/playoffRounds`));
  const bracketPlayed = Object.values(existingBracket.val() || {}).reduce(
    (n: number, r: any) =>
      n +
      Object.values(r?.matches || {}).filter(
        (m: any) => m?.status === 'completed' && m?.player2
      ).length,
    0
  );
  if (bracketPlayed > 0) {
    throw new Error(
      `Refusing to regenerate the bracket: ${bracketPlayed} match(es) have already been played and would be erased.`
    );
  }

  const playoffRounds = buildPlayoffRounds(orderedParticipants, 'r');
  await set(ref(database, `tournaments/${tournamentId}/playoffRounds`), playoffRounds);
  await update(ref(database, `tournaments/${tournamentId}`), { status: 'active' });
};

// ─── Standings ────────────────────────────────────────────────────────────────

export interface GroupStanding {
  uid: string;
  name: string;
  played: number;
  wins: number;
  losses: number;
  points: number;
  triplesAvg: number;
  accuracy: number;
  /** Games (erä) won across all matches — a match can span several games */
  roundsWon: number;
  /** Games (erä) lost across all matches */
  roundsLost: number;
  /** Erä +/- : roundsWon − roundsLost (organizer's primary tiebreaker) */
  roundDiff: number;
  /** Total triplets thrown (Triplat) */
  triplesTotal: number;
  /** Total dice frozen (FREEZE) */
  freezes: number;
  /** Piste K/A : standings points per game/round played */
  pointsPerRound: number;
}

export const computeGroupStandings = (
  groupId: string,
  playerIds: string[],
  poolMatches: { [matchId: string]: TournamentMatch },
  participants: { [uid: string]: TournamentParticipant }
): GroupStanding[] => {
  const standings: { [uid: string]: GroupStanding } = {};

  playerIds.forEach((uid) => {
    standings[uid] = {
      uid,
      name: participants[uid]?.name || uid,
      played: 0,
      wins: 0,
      losses: 0,
      points: 0,
      triplesAvg: 0,
      accuracy: 0,
      roundsWon: 0,
      roundsLost: 0,
      roundDiff: 0,
      triplesTotal: 0,
      freezes: 0,
      pointsPerRound: 0,
    };
  });

  const groupMatches = Object.values(poolMatches).filter(
    (m) => m.groupId === groupId && m.status === 'completed' && m.result
  );

  groupMatches.forEach((match) => {
    const r = match.result!;
    const p1uid = match.player1.uid;
    const p2uid = match.player2?.uid;
    if (!p2uid) return;

    [p1uid, p2uid].forEach((uid) => {
      if (!standings[uid]) return;
      const s = standings[uid];
      s.played++;
      s.points += r.points[uid] || 0;
      if (r.winner === uid) s.wins++;
      else s.losses++;
      // Games (erä) won/lost within this match, from the stored leg score.
      const legsWon = uid === p1uid ? r.player1Wins : r.player2Wins;
      const legsLost = uid === p1uid ? r.player2Wins : r.player1Wins;
      s.roundsWon += legsWon || 0;
      s.roundsLost += legsLost || 0;
      const legs = (legsWon || 0) + (legsLost || 0);
      const st = r.stats?.[uid];
      if (st) {
        // accuracy stays as a sum here, averaged over `played` below
        s.accuracy += st.accuracy;
        // Prefer the raw triplet total; fall back to avg×games for older results
        s.triplesTotal += st.triples ?? (st.triplesAvg * (legs || 1));
        s.freezes += st.freezes ?? 0;
      }
    });
  });

  // Derive the averaged / per-round columns
  Object.values(standings).forEach((s) => {
    const totalRounds = s.roundsWon + s.roundsLost;
    s.roundDiff = s.roundsWon - s.roundsLost;
    if (s.played > 0) s.accuracy = s.accuracy / s.played;
    s.triplesAvg = totalRounds > 0 ? s.triplesTotal / totalRounds : 0;
    s.pointsPerRound = totalRounds > 0 ? s.points / totalRounds : 0;
  });

  const getH2H = (uid1: string, uid2: string): number => {
    const m = groupMatches.find(
      (m) =>
        (m.player1.uid === uid1 && m.player2?.uid === uid2) ||
        (m.player1.uid === uid2 && m.player2?.uid === uid1)
    );
    if (!m?.result) return 0;
    return m.result.winner === uid1 ? 1 : -1;
  };

  return Object.values(standings).sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    // Erä +/- is the organizer's primary tiebreaker
    if (b.roundDiff !== a.roundDiff) return b.roundDiff - a.roundDiff;
    const h2h = getH2H(a.uid, b.uid);
    if (h2h !== 0) return -h2h;
    if (b.triplesAvg !== a.triplesAvg) return b.triplesAvg - a.triplesAvg;
    return b.accuracy - a.accuracy;
  });
};

// ─── Final placements ─────────────────────────────────────────────────────────

export interface TournamentPlacement {
  uid: string;
  name: string;
  /** 1-based final placement; eliminated players share their band's start (both semifinal losers = 3) */
  placement: number;
}

/**
 * Final placements of a COMPLETED tournament, per the game designer's league
 * model: league points come from these placements only — never from individual
 * match points. Playoff players place by the round they were eliminated in
 * (final loser = 2nd, semifinal losers = 3rd, quarterfinal losers = 5th, …);
 * pool players who missed the playoffs rank below them by group finish.
 */
export const computeTournamentPlacements = (t: Tournament): TournamentPlacement[] => {
  if (t.status !== 'completed') return [];
  const placements: TournamentPlacement[] = [];
  const placed = new Set<string>();

  const rounds = Object.values(t.playoffRounds || {});
  if (rounds.length > 0) {
    const maxRound = Math.max(...rounds.map((r) => r.roundIndex));
    const lastRound: { [uid: string]: number } = {};
    const names: { [uid: string]: string } = {};

    rounds.forEach((round) => {
      Object.values(round.matches || {}).forEach((m: any) => {
        [m?.player1, m?.player2].forEach((p: any) => {
          if (!p?.uid) return;
          names[p.uid] = p.name || t.participants?.[p.uid]?.name || p.uid;
          if (lastRound[p.uid] === undefined || round.roundIndex > lastRound[p.uid]) {
            lastRound[p.uid] = round.roundIndex;
          }
        });
      });
    });

    Object.entries(lastRound).forEach(([uid, r]) => {
      let placement: number;
      if (r === maxRound) {
        placement = t.winner?.uid === uid ? 1 : 2;
      } else {
        // Eliminated in round r → 2^(maxRound − r) players were still ahead
        placement = Math.pow(2, maxRound - r) + 1;
      }
      placements.push({ uid, name: names[uid], placement });
      placed.add(uid);
    });
  }

  // Pool players who didn't reach the playoffs: below all playoff players,
  // ordered by their finishing position within their group, then points.
  if (t.groups && t.poolMatches) {
    const rest: { uid: string; name: string; groupRank: number; points: number }[] = [];
    Object.values(t.groups).forEach((group) => {
      const standings = computeGroupStandings(
        group.id, group.playerIds, t.poolMatches!, t.participants
      );
      standings.forEach((s, idx) => {
        if (!placed.has(s.uid)) {
          rest.push({ uid: s.uid, name: s.name, groupRank: idx, points: s.points });
        }
      });
    });
    rest.sort((a, b) => a.groupRank - b.groupRank || b.points - a.points);
    let next = placed.size + 1;
    rest.forEach((p) => {
      placements.push({ uid: p.uid, name: p.name, placement: next });
      next += 1;
      placed.add(p.uid);
    });
  }

  return placements.sort((a, b) => a.placement - b.placement);
};

// ─── Match lifecycle ──────────────────────────────────────────────────────────

/**
 * Atomically claim a match for play. Returns true if this caller acquired it,
 * false if it was already active (another device started it) or completed.
 * Uses a transaction so two devices can't both start the same match.
 */
/**
 * RTDB path of a match. Pool matches are keyed by their id; playoff matches by
 * matchIndex within the round. Using the id for a playoff match writes a rogue
 * partial entry (no players) that crashes the screens — hence the white page.
 */
export const matchPathFor = (tournamentId: string, match: TournamentMatch): string =>
  match.phase === 'pool'
    ? `tournaments/${tournamentId}/poolMatches/${match.id}`
    : `tournaments/${tournamentId}/playoffRounds/${match.roundIndex}/matches/${match.matchIndex}`;

/** A claim with no sign of life for this long can be taken over by anyone. */
export const STALE_CLAIM_MS = 10 * 60 * 1000;

/**
 * Why a claim attempt failed — the caller must NOT report a network error as
 * "someone else started it". That wrong message was shown for any failure,
 * including a dropped connection, and sent players looking for a phantom
 * second device.
 */
export type ClaimOutcome = 'claimed' | 'already-active' | 'error';

export const startTournamentMatch = async (
  tournamentId: string,
  match: TournamentMatch,
  claimantUid?: string
): Promise<ClaimOutcome> => {
  const path = matchPathFor(tournamentId, match);

  let result;
  try {
    result = await runTransaction(ref(database, `${path}/status`), (current) => {
      if (current === 'active' || current === 'completed') return; // already taken — abort
      return 'active';
    });
  } catch {
    return 'error';
  }

  if (!result.committed) return 'already-active';

  // Record WHO holds it. Without this the app cannot tell "your own device
  // already has this open" from "an abandoned claim nobody can get back into".
  await update(ref(database, path), {
    startedAt: Date.now(),
    ...(claimantUid ? { claimedBy: claimantUid } : {}),
  }).catch(() => {});
  return 'claimed';
};

/**
 * Hand an already-active match back to a player.
 *
 * Re-running the claim transaction cannot do this: it aborts on 'active', which
 * is exactly why an abandoned match was unplayable by anyone until an admin
 * reset it. Taking over only refreshes the holder — the result is saved against
 * the match, not against whoever claimed it, so nothing else has to change.
 */
export const takeOverTournamentMatch = async (
  tournamentId: string,
  match: TournamentMatch,
  claimantUid: string
): Promise<void> => {
  const path = matchPathFor(tournamentId, match);
  // Check the SERVER, not the passed-in snapshot. A device with a lagging
  // subscription would otherwise re-open a match that has just been completed
  // elsewhere — flipping it back to 'active' with its result still attached.
  // Refusing completed matches is the one promise take-over makes, so it must
  // not be decided on stale data.
  const current = await get(ref(database, `${path}/status`));
  if (current.val() === 'completed') {
    throw new Error('That match has just been completed on another device.');
  }
  await update(ref(database, path), {
    status: 'active',
    claimedBy: claimantUid,
    startedAt: match.startedAt || Date.now(),
  });
};

/**
 * Give a claimed match back to the pool of playable matches.
 *
 * Quitting a game used to wipe the local game state and leave `status:'active'`
 * on the server with no way back in for either player. Releasing is safe: no
 * result has been recorded, so the match simply becomes playable again.
 */
export const releaseTournamentMatch = async (
  tournamentId: string,
  match: TournamentMatch
): Promise<void> => {
  if (match.status === 'completed') return; // never reopen a recorded result
  const path = matchPathFor(tournamentId, match);
  await update(ref(database, path), {
    status: 'pending',
    startedAt: null,
    claimedBy: null,
  });
  await remove(ref(database, `tournaments/${tournamentId}/liveScores/${match.id}`)).catch(() => {});
};

/**
 * Release by context alone — used from the game screen, which carries a
 * TournamentMatchContext rather than the match object.
 */
export const releaseTournamentMatchByContext = async (ctx: {
  tournamentId: string;
  matchId: string;
  phase: 'pool' | 'playoff';
  roundIndex?: number;
  matchIndex?: number;
}): Promise<void> => {
  const path =
    ctx.phase === 'pool'
      ? `tournaments/${ctx.tournamentId}/poolMatches/${ctx.matchId}`
      : `tournaments/${ctx.tournamentId}/playoffRounds/${ctx.roundIndex}/matches/${ctx.matchIndex}`;

  const snap = await get(ref(database, `${path}/status`));
  if (snap.val() === 'completed') return; // the result landed — leave it alone
  await update(ref(database, path), { status: 'pending', startedAt: null, claimedBy: null });
  await remove(ref(database, `tournaments/${ctx.tournamentId}/liveScores/${ctx.matchId}`)).catch(() => {});
};

/**
 * Last sign of life for a claimed match. Live scores are already written on
 * every score change, so they double as the heartbeat — no extra traffic.
 */
export const matchLastSeenAt = (
  tournament: Tournament,
  match: TournamentMatch
): number => {
  const live = tournament.liveScores?.[match.id] as { [k: string]: number | string } | undefined;
  const hb = Number(live?._hbAt) || 0;
  return Math.max(hb, match.startedAt || 0);
};

export const isClaimStale = (tournament: Tournament, match: TournamentMatch): boolean =>
  match.status === 'active' && Date.now() - matchLastSeenAt(tournament, match) > STALE_CLAIM_MS;

/**
 * Reset a stuck/abandoned match back to 'pending' so it can be re-started.
 * Use when a player opened a match but never saved a result (it would otherwise
 * stay 'active' forever and block that player from every other match). Clears
 * any leftover live scores. Only meaningful for non-completed matches.
 */
export const resetTournamentMatch = async (
  tournamentId: string,
  match: TournamentMatch
): Promise<void> => {
  const path = matchPathFor(tournamentId, match);

  // Resetting a COMPLETED match would leave its result in place while the
  // winner has already advanced — a contradictory state that has to be
  // untangled by hand. Correct the result instead.
  const current = await get(ref(database, `${path}/status`));
  if (current.val() === 'completed') {
    throw new Error('That match is already completed — correct its result instead of resetting it.');
  }

  await update(ref(database, path), { status: 'pending', startedAt: null, claimedBy: null });
  if (match.id) {
    await remove(ref(database, `tournaments/${tournamentId}/liveScores/${match.id}`));
  }
};

/**
 * Admin no-show handling: award the match to `winnerUid` without playing it.
 * Routes through the normal completion functions, so standings, history,
 * bracket advancement and champion crowning all happen exactly as for a played
 * match — but the result is flagged `walkover`, which skips ELO (no game was
 * played). Winner receives clean-sweep points (3), loser 0.
 * Without this, one no-show wedges the whole tournament: their pool matches can
 * never complete and "Generate Playoffs" requires every match done.
 */
/**
 * Standings points for a leg score. Mirrors WinnerScreen's scheme exactly —
 * a clean sweep is worth more than a win that dropped a game, and a loser who
 * took a game still scores. Keep the two in step: standings, the pool cut and
 * every tiebreaker below points are computed from these.
 */
export const pointsForLegScore = (
  winnerUid: string,
  loserUid: string,
  loserLegs: number
): { [uid: string]: number } => ({
  [winnerUid]: loserLegs === 0 ? 3 : 2,
  [loserUid]: loserLegs > 0 ? 1 : 0,
});

/** An explicit leg score an admin dialled in, e.g. 2–1. */
export interface AdminLegScore {
  winnerLegs: number;
  loserLegs: number;
}

export const awardWalkover = async (
  tournamentId: string,
  match: TournamentMatch,
  winnerUid: string,
  tournament: Tournament,
  /**
   * Optional real score. A no-show is a clean forfeit, but a match that was
   * actually played and then lost to a bug is not — recording it 1–0 when it
   * finished 2–1 silently changes erä +/-, which is the FIRST tiebreaker after
   * points and decides who advances. Defaults to the forfeit shape.
   */
  score?: AdminLegScore
): Promise<void> => {
  const p2 = match.player2;
  // These used to `return` silently: the admin saw no error, assumed the
  // walkover had been awarded, and the bracket quietly did not advance.
  if (!p2) throw new Error('That match has no opponent to forfeit (it is a bye or a half-filled slot). Fill the empty slot first.');
  if (winnerUid !== match.player1.uid && winnerUid !== p2.uid) {
    throw new Error('The winner must be one of the two players in this match.');
  }
  const loserUid = winnerUid === match.player1.uid ? p2.uid : match.player1.uid;

  const winnerLegs = score ? Math.max(0, Math.floor(score.winnerLegs)) : 1;
  const loserLegs = score ? Math.max(0, Math.floor(score.loserLegs)) : 0;
  if (score && loserLegs >= winnerLegs) {
    throw new Error('The winner must have more games than the loser.');
  }

  const result: TournamentMatchResult = {
    winner: winnerUid,
    player1Wins: winnerUid === match.player1.uid ? winnerLegs : loserLegs,
    player2Wins: winnerUid === p2.uid ? winnerLegs : loserLegs,
    points: pointsForLegScore(winnerUid, loserUid, loserLegs),
    walkover: true,
  };

  // Clear any half-played live scores before completing
  if (match.id) {
    await remove(ref(database, `tournaments/${tournamentId}/liveScores/${match.id}`)).catch(() => {});
  }

  if (match.phase === 'pool') {
    await completeTournamentPoolMatch(tournamentId, match.id, result, tournament);
  } else if (match.roundIndex !== undefined && match.matchIndex !== undefined) {
    await completeTournamentPlayoffMatch(
      tournamentId, match.roundIndex, match.matchIndex, result, tournament
    );
  }
};

// ─── Admin bracket repair ─────────────────────────────────────────────────────
// Escape hatches for the two live-event failure modes that otherwise need
// manual DB surgery: a lost advancement (half-filled bracket slot) and a
// wrongly-saved result.

/**
 * Fill (or replace) one player slot of a NOT-completed playoff match.
 * Writes only the slot plus idempotent structural fields — merge-safe like
 * normal advancement.
 */
/**
 * Re-run advancement across the whole bracket.
 *
 * A winner reaches the next round through a single client-side write at the
 * moment their match is saved. If that write is lost — flaky wifi, a walkover
 * awarded on a match that was already recorded, a result repaired by hand —
 * the next-round slot silently stays empty. Nothing retries it, and the match
 * then shows up for NEITHER player: getPlayerActiveMatch skips matches that
 * are missing a slot, so the player who advanced sees nothing and their
 * would-be opponent has no match at all. That is how a final can vanish.
 *
 * This walks every completed playoff match and re-asserts where its winner
 * belongs. Idempotent and merge-only: a slot that is already correct is left
 * alone, and a next-round match that has been played is never disturbed.
 */
export interface BracketRepairReport {
  fixed: string[];
  alreadyCorrect: number;
  skipped: string[];
  crowned: string | null;
}

export const repairBracketAdvancement = async (
  tournamentId: string,
  tournament: Tournament
): Promise<BracketRepairReport> => {
  const report: BracketRepairReport = { fixed: [], alreadyCorrect: 0, skipped: [], crowned: null };
  const rounds = Object.values(tournament.playoffRounds || {}).sort(
    (a, b) => a.roundIndex - b.roundIndex
  );
  if (!rounds.length) return report;

  for (const round of rounds) {
    const matches = Object.values(round.matches || {}) as TournamentMatch[];
    for (const m of matches) {
      if (m.status !== 'completed' || !m.result?.winner) continue;
      if (m.matchIndex === undefined || m.roundIndex === undefined) continue;

      const winnerUid = m.result.winner;
      // Same name resolution the completion path uses: the match's own slots
      // first, so a stale participants map can never put a uid on screen.
      const winnerName =
        (m.player1?.uid === winnerUid && m.player1?.name) ||
        (m.player2?.uid === winnerUid && m.player2?.name) ||
        tournament.participants?.[winnerUid]?.name ||
        winnerUid;

      const nextRoundIndex = m.roundIndex + 1;
      const { nextMatchIndex, slotKey } = nextBracketSlot(m.matchIndex);
      const hasNextRound = !!tournament.playoffRounds?.[`${nextRoundIndex}`];
      const label = `${round.name} #${m.matchIndex + 1}`;

      if (!hasNextRound) {
        // The final. Declare the champion if it never happened.
        if (tournament.status !== 'completed' || tournament.winner?.uid !== winnerUid) {
          await update(ref(database), {
            [`tournaments/${tournamentId}/status`]: 'completed',
            [`tournaments/${tournamentId}/winner`]: { uid: winnerUid, name: winnerName },
          });
          if (tournament.isChampionship) {
            await crownChampion(tournamentId, tournament.name, winnerUid, winnerName).catch(() => {});
          }
          report.crowned = winnerName;
        }
        continue;
      }

      const nextPath = `tournaments/${tournamentId}/playoffRounds/${nextRoundIndex}/matches/${nextMatchIndex}`;
      const nextSnap = await get(ref(database, nextPath));
      const next = nextSnap.exists() ? nextSnap.val() : null;

      if (next?.status === 'completed' || next?.status === 'active') {
        if (next?.[slotKey]?.uid !== winnerUid) {
          report.skipped.push(
            `${label}: next match is already ${next.status} with someone else in the slot — correct results from the latest round backwards.`
          );
        } else {
          report.alreadyCorrect++;
        }
        continue;
      }

      if (next?.[slotKey]?.uid === winnerUid) {
        report.alreadyCorrect++;
        continue;
      }

      const slotUpdate: { [key: string]: any } = { [slotKey]: { uid: winnerUid, name: winnerName } };
      if (!next) {
        slotUpdate.id = `r${nextRoundIndex}_m${nextMatchIndex}`;
        slotUpdate.roundIndex = nextRoundIndex;
        slotUpdate.matchIndex = nextMatchIndex;
        slotUpdate.status = 'pending';
        slotUpdate.phase = 'playoff';
      }
      await update(ref(database, nextPath), slotUpdate);
      report.fixed.push(`${winnerName} → next round (from ${label})`);
    }
  }

  return report;
};

export const adminSetPlayoffSlot = async (
  tournamentId: string,
  roundIndex: number,
  matchIndex: number,
  slotKey: 'player1' | 'player2',
  player: { uid: string; name: string }
): Promise<void> => {
  const path = `tournaments/${tournamentId}/playoffRounds/${roundIndex}/matches/${matchIndex}`;
  const snap = await get(ref(database, path));
  if (snap.exists() && snap.val()?.status === 'completed') {
    throw new Error('That match is already completed — correct its result instead.');
  }
  // A player in BOTH slots is unplayable — the game needs two people — and the
  // only way out would be a walkover.
  const otherSlot = slotKey === 'player1' ? 'player2' : 'player1';
  if (snap.exists() && snap.val()?.[otherSlot]?.uid === player.uid) {
    throw new Error(`${player.name} is already in the other slot of this match.`);
  }

  const upd: { [key: string]: any } = { [slotKey]: player };
  if (!snap.exists()) {
    upd.id = `r${roundIndex}_m${matchIndex}`;
    upd.roundIndex = roundIndex;
    upd.matchIndex = matchIndex;
    upd.status = 'pending';
    upd.phase = 'playoff';
  }
  await update(ref(database, path), upd);
};

/**
 * Correct a wrongly-saved result: set a new winner on a COMPLETED match.
 * Keeps the recorded per-player stats, reassigns the leg score so the larger
 * count follows the corrected winner, and recomputes standings points
 * (3/0 sweep, 2/1 contested). For playoff matches it also repairs the next
 * round — the old winner is swapped for the new one in the slot they advanced
 * to — and, on the final, fixes the tournament winner (and champion).
 * Refused when the next-round match is already completed or being played:
 * correct from the latest round backwards.
 *
 * ELO is NOT auto-corrected here — run the admin ELO recalculation afterwards.
 */
export const adminOverrideMatchResult = async (
  tournamentId: string,
  match: TournamentMatch,
  newWinnerUid: string,
  tournament: Tournament,
  /**
   * Optional corrected leg score. Without it only the winner is swapped and the
   * existing leg counts are reassigned, which means a result that recorded the
   * right winner but the wrong SCORE could not be fixed from the UI at all
   * (the same-winner early return below). Passing a score also makes that case
   * correctable, since erä +/- decides the pool cut.
   */
  score?: AdminLegScore
): Promise<void> => {
  const p2 = match.player2;
  if (!p2) throw new Error('Bye matches cannot be corrected.');
  if (match.status !== 'completed' || !match.result) {
    throw new Error('That match has no saved result to correct.');
  }
  if (newWinnerUid !== match.player1.uid && newWinnerUid !== p2.uid) {
    throw new Error('The winner must be one of the match players.');
  }
  const old = match.result;

  // Reassign leg counts: the larger count follows the corrected winner, unless
  // the admin dialled in an explicit score.
  let hi = Math.max(old.player1Wins || 0, old.player2Wins || 0) || 1;
  let lo = Math.min(old.player1Wins || 0, old.player2Wins || 0);
  if (score) {
    hi = Math.max(0, Math.floor(score.winnerLegs));
    lo = Math.max(0, Math.floor(score.loserLegs));
    if (lo >= hi) throw new Error('The winner must have more games than the loser.');
  }

  const sameWinner = old.winner === newWinnerUid;
  const sameScore =
    (newWinnerUid === match.player1.uid ? hi : lo) === (old.player1Wins || 0) &&
    (newWinnerUid === p2.uid ? hi : lo) === (old.player2Wins || 0);
  if (sameWinner && sameScore) return; // nothing to change

  const loserUid = newWinnerUid === match.player1.uid ? p2.uid : match.player1.uid;
  const newWinnerName = tournament.participants?.[newWinnerUid]?.name
    || (newWinnerUid === match.player1.uid ? match.player1.name : p2.name);

  // Drop stale ratingChanges — they applied to the wrong winner (admin recalc fixes ELO).
  const { ratingChanges: _stale, ...oldRest } = old;
  const corrected: TournamentMatchResult = {
    ...oldRest,
    winner: newWinnerUid,
    player1Wins: newWinnerUid === match.player1.uid ? hi : lo,
    player2Wins: newWinnerUid === p2.uid ? hi : lo,
    points: pointsForLegScore(newWinnerUid, loserUid, lo),
  };

  const isPlayoff = match.phase === 'playoff'
    && match.roundIndex !== undefined && match.matchIndex !== undefined;
  const matchPath = isPlayoff
    ? `tournaments/${tournamentId}/playoffRounds/${match.roundIndex}/matches/${match.matchIndex}`
    : `tournaments/${tournamentId}/poolMatches/${match.id}`;

  // Validate downstream BEFORE writing anything, so a refusal leaves no partial state.
  // A score-only correction leaves the winner — and therefore the whole bracket
  // below it — untouched, so none of the downstream checks apply.
  let nextRepair: (() => Promise<void>) | null = null;
  if (isPlayoff && !sameWinner) {
    const nextRoundIndex = (match.roundIndex as number) + 1;
    const { nextMatchIndex, slotKey } = nextBracketSlot(match.matchIndex as number);
    const hasNextRound = !!tournament.playoffRounds?.[`${nextRoundIndex}`];
    if (hasNextRound) {
      const nextPath = `tournaments/${tournamentId}/playoffRounds/${nextRoundIndex}/matches/${nextMatchIndex}`;
      const nextSnap = await get(ref(database, nextPath));
      const next = nextSnap.exists() ? nextSnap.val() : null;
      if (next?.status === 'completed') {
        throw new Error('The next-round match is already completed — correct results from the latest round backwards.');
      }
      if (next?.status === 'active') {
        throw new Error('The next-round match is being played right now — reset it first, then correct this result.');
      }
      nextRepair = async () => {
        await update(ref(database, nextPath), {
          [slotKey]: { uid: newWinnerUid, name: newWinnerName },
        });
      };
    } else {
      // This was the final — fix the tournament winner (and champion title).
      // Also force status to 'completed': the podium and the "Completed" badge
      // both require status AND winner, so setting the winner alone would leave
      // the broadcast screen stuck on the live view. This doubles as the
      // in-app repair for a final whose champion write was denied.
      nextRepair = async () => {
        await update(ref(database), {
          [`tournaments/${tournamentId}/status`]: 'completed',
          [`tournaments/${tournamentId}/winner`]: { uid: newWinnerUid, name: newWinnerName },
        });
        if (tournament.isChampionship) {
          await crownChampion(tournamentId, tournament.name, newWinnerUid, newWinnerName);
        }
      };
    }
  }

  await update(ref(database, matchPath), { result: corrected });
  if (nextRepair) await nextRepair();

  // Fix both players' tournamentHistory entries (won flag + points).
  const logMatchId = isPlayoff ? `r${match.roundIndex}_m${match.matchIndex}` : match.id;
  const historyUpdates: { [path: string]: any } = {};
  [match.player1.uid, p2.uid].forEach((uid) => {
    historyUpdates[`users/${uid}/tournamentHistory/${logMatchId}/won`] = uid === newWinnerUid;
    historyUpdates[`users/${uid}/tournamentHistory/${logMatchId}/points`] = corrected.points[uid] ?? 0;
  });
  await update(ref(database), historyUpdates).catch(() => {});
};

export const completeTournamentPoolMatch = async (
  tournamentId: string,
  matchId: string,
  result: TournamentMatchResult,
  tournament?: Tournament,
  /**
   * Set by a RETRY after the save could not be confirmed on the server.
   *
   * The guard below reads `status`, and that read can be served from the local
   * cache — which already holds the optimistic copy of a write that never
   * reached the backend. Without this escape hatch a retry would keep
   * short-circuiting on the cached 'completed' and the result could never be
   * saved at all. Re-writing is safe: the paths and values are identical, and
   * applyMatchRating is keyed per match so ELO is not applied twice.
   */
  force = false
): Promise<void> => {
  // Idempotency guard — if already completed (reload / second device), do nothing.
  if (!force) {
    const statusSnap = await get(ref(database, `tournaments/${tournamentId}/poolMatches/${matchId}/status`));
    if (statusSnap.val() === 'completed') return;
  }

  const now = Date.now();
  await update(ref(database, `tournaments/${tournamentId}/poolMatches/${matchId}`), {
    status: 'completed',
    result,
    completedAt: now,
  });
  await remove(ref(database, `tournaments/${tournamentId}/liveScores/${matchId}`));

  // Log to each player's tournament history
  if (tournament) {
    const match = tournament.poolMatches?.[matchId];
    if (match) {
      const p1uid = match.player1.uid;
      const p2uid = match.player2?.uid;
      const profileUpdates: { [path: string]: any } = {};
      [
        { uid: p1uid, opponent: match.player2 },
        ...(p2uid ? [{ uid: p2uid, opponent: match.player1 }] : []),
      ].forEach(({ uid, opponent }) => {
        if (!uid) return;
        profileUpdates[`users/${uid}/tournamentHistory/${matchId}`] = {
          tournamentId,
          tournamentName: tournament.name,
          matchId,
          phase: 'pool',
          opponentUid: opponent?.uid || null,
          opponentName: opponent?.name || null,
          won: result.winner === uid,
          points: result.points[uid] ?? 0,
          completedAt: now,
        };
      });
      if (Object.keys(profileUpdates).length) {
        await update(ref(database), profileUpdates);
      }

      // ELO update — runs after the result is final; failures never affect
      // match completion. Tournament matches are the only source of rating.
      // Walkovers skip rating entirely: no game was played.
      if (!result.walkover) try {
        await applyMatchRating(
          tournament,
          { ...match, completedAt: now },
          result,
          `tournaments/${tournamentId}/poolMatches/${matchId}`
        );
      } catch (e) {
        console.error('Rating update failed (pool match):', e);
      }
    }
  }
};

export const completeTournamentPlayoffMatch = async (
  tournamentId: string,
  roundIndex: number,
  matchIndex: number,
  result: TournamentMatchResult,
  tournament: Tournament,
  /** See the `force` note on completeTournamentPoolMatch. */
  force = false
): Promise<void> => {
  const matchPath = `tournaments/${tournamentId}/playoffRounds/${roundIndex}/matches/${matchIndex}`;

  // RECORDING the result happens once; PROPAGATING it (advancing the winner or
  // crowning the champion) is idempotent and must run on EVERY call.
  //
  // This used to bail out entirely when the match was already completed. That
  // made a partial save unrecoverable: if the result wrote but the follow-up
  // failed — which happened for real, because declaring the champion writes to
  // the admin-only tournament root from a *player's* device — then pressing
  // RETRY SAVE short-circuited here and reported success, stranding the winner
  // and leaving the tournament with no champion. Re-running the propagation is
  // safe (same winner into the same slot) and self-heals that state.
  const liveSnap = await get(ref(database, matchPath));
  const liveMatch = liveSnap.exists() ? liveSnap.val() : null;
  // `force` is set by a retry whose save could not be confirmed on the server:
  // this read can come from the local cache, which may already hold an
  // optimistic copy of a write the backend never received.
  const alreadyRecorded = !force && liveMatch?.status === 'completed';

  if (!alreadyRecorded) {
  await update(ref(database, matchPath), {
    status: 'completed',
    result,
    completedAt: Date.now(),
  });

  // Clean up live scores under BOTH keys. Live scores are written under the
  // match's stored `id`, but pool-generated playoff round 0 uses a
  // `playoff_r0_m*` id while this computed key is `r0_m*` — so cleaning only
  // the computed one left a stale entry behind on every pool semifinal, which
  // then shows up as noise in the Monitor's health checks during exactly the
  // phase the organiser is watching them.
  const matchId = `r${roundIndex}_m${matchIndex}`;
  await remove(ref(database, `tournaments/${tournamentId}/liveScores/${matchId}`));
  const storedId = liveMatch?.id;
  if (storedId && storedId !== matchId) {
    await remove(ref(database, `tournaments/${tournamentId}/liveScores/${storedId}`)).catch(() => {});
  }

  // Log to each player's tournament history
  const matchObj = tournament.playoffRounds?.[`${roundIndex}`]?.matches?.[`${matchIndex}`];
  if (matchObj) {
    const p1uid = matchObj.player1?.uid;
    const p2uid = matchObj.player2?.uid;
    const profileUpdates: { [path: string]: any } = {};
    const now2 = Date.now();
    const logMatchId = `r${roundIndex}_m${matchIndex}`;
    [
      { uid: p1uid, opponent: matchObj.player2 },
      ...(p2uid ? [{ uid: p2uid, opponent: matchObj.player1 }] : []),
    ].forEach(({ uid, opponent }) => {
      if (!uid) return;
      profileUpdates[`users/${uid}/tournamentHistory/${logMatchId}`] = {
        tournamentId,
        tournamentName: tournament.name,
        matchId: logMatchId,
        phase: 'playoff',
        roundName: tournament.playoffRounds?.[`${roundIndex}`]?.name || `Round ${roundIndex + 1}`,
        opponentUid: opponent?.uid || null,
        opponentName: opponent?.name || null,
        won: result.winner === uid,
        points: result.points[uid] ?? 0,
        completedAt: now2,
      };
    });
    if (Object.keys(profileUpdates).length) {
      await update(ref(database), profileUpdates);
    }

    // ELO update — after the result is final, never blocks advancement.
    // Walkovers skip rating entirely: no game was played.
    if (!result.walkover) try {
      await applyMatchRating(
        tournament,
        { ...matchObj, completedAt: Date.now() },
        result,
        `tournaments/${tournamentId}/playoffRounds/${roundIndex}/matches/${matchIndex}`
      );
    } catch (e) {
      console.error('Rating update failed (playoff match):', e);
    }
  }
  } // end: record once

  // ── Propagation (idempotent — runs on retries too) ───────────────────────
  const nextRoundIndex = roundIndex + 1;
  const { nextMatchIndex, slotKey } = nextBracketSlot(matchIndex);

  const winnerUid = result.winner;
  // Prefer the names on the match itself (fresh from the DB) — the passed-in
  // tournament snapshot can be stale or, on a retry, missing entirely, and
  // falling through to a raw uid would put a uid on the broadcast screen.
  const winnerName =
    (liveMatch?.player1?.uid === winnerUid ? liveMatch?.player1?.name : undefined) ||
    (liveMatch?.player2?.uid === winnerUid ? liveMatch?.player2?.name : undefined) ||
    tournament.participants?.[winnerUid]?.name ||
    winnerUid;

  // Is there a next round? Read fresh rather than trusting the prop: getting
  // this wrong either strands the winner or crowns a champion mid-bracket.
  const nextRoundSnap = await get(
    ref(database, `tournaments/${tournamentId}/playoffRounds/${nextRoundIndex}`)
  );

  if (!nextRoundSnap.exists()) {
    // This was the final — declare the champion.
    // Written as explicit LEAF paths (not an update on the tournament root) so
    // the per-field `status`/`winner` rules apply. The root itself is
    // admin-only, and the device saving the final belongs to a player.
    await update(ref(database), {
      [`tournaments/${tournamentId}/status`]: 'completed',
      [`tournaments/${tournamentId}/winner`]: { uid: winnerUid, name: winnerName },
    });

    // Crown champion if this is a championship tournament
    if (tournament.isChampionship) {
      await crownChampion(tournamentId, tournament.name, winnerUid, winnerName);
    }
    return;
  }

  const nextMatchPath = `tournaments/${tournamentId}/playoffRounds/${nextRoundIndex}/matches/${nextMatchIndex}`;

  // Merge-only advancement. NEVER set() the whole next-match object: when two
  // sibling matches complete near-simultaneously on different devices, the later
  // device's subscribed tournament state may not include the sibling's
  // advancement yet — a set() would erase that winner from the bracket with no
  // in-app recovery. Instead: fresh read (not the possibly-stale prop), then
  // update() which creates missing paths and only touches the keys given. Each
  // device writes only its own slotKey plus idempotent structural constants, so
  // concurrent advancements merge instead of clobbering.
  const freshSnap = await get(ref(database, nextMatchPath));
  const slotUpdate: { [key: string]: any } = {
    [slotKey]: { uid: winnerUid, name: winnerName },
  };
  if (!freshSnap.exists()) {
    slotUpdate.id = `r${nextRoundIndex}_m${nextMatchIndex}`;
    slotUpdate.roundIndex = nextRoundIndex;
    slotUpdate.matchIndex = nextMatchIndex;
    slotUpdate.status = 'pending';
    slotUpdate.phase = 'playoff';
  }
  await update(ref(database, nextMatchPath), slotUpdate);
};

// ─── Playoffs from Pool ───────────────────────────────────────────────────────

/**
 * Which players advance from the pool, in bracket-seed order: group winners
 * first, then runners-up, etc.; within each rank tier ordered by pool points
 * (merit — so byes go to the strongest group winners). Pure — shared by the
 * real playoff generator and the offline simulator.
 */
export const computePoolPlayoffSeeds = (
  tournament: Tournament
): { uid: string; name: string }[] => {
  const { groups, poolMatches, poolSettings, participants } = tournament;
  if (!groups || !poolSettings || !participants) return [];

  const seededPlayers: { uid: string; name: string }[] = [];
  for (let rank = 0; rank < poolSettings.advanceCount; rank++) {
    const tier: { uid: string; name: string; points: number }[] = [];
    Object.keys(groups)
      .sort()
      .forEach((gid) => {
        const group = groups[gid];
        const standings = computeGroupStandings(gid, group.playerIds, poolMatches || {}, participants);
        const advancingPlayer = standings[rank];
        if (advancingPlayer) {
          tier.push({ uid: advancingPlayer.uid, name: advancingPlayer.name, points: advancingPlayer.points });
        }
      });
    tier.sort((a, b) => b.points - a.points);
    tier.forEach((p) => seededPlayers.push({ uid: p.uid, name: p.name }));
  }
  return seededPlayers;
};

export const generatePoolPlayoffs = async (
  tournamentId: string,
  tournament: Tournament
): Promise<void> => {
  if (!tournament.groups || !tournament.poolSettings || !tournament.participants) {
    throw new Error('Missing tournament data');
  }
  const seeds = computePoolPlayoffSeeds(tournament);
  // A bracket with <2 players has no playable match (a lone advancer becomes a
  // pre-completed bye that never triggers completion) — the tournament would be
  // stuck in 'playoffs' forever. Refuse instead.
  if (seeds.length < 2) {
    throw new Error(
      `Playoffs need at least 2 advancing players — the current settings produce ${seeds.length}. ` +
      'Increase groups × advance count.'
    );
  }
  const existingRounds = await get(ref(database, `tournaments/${tournamentId}/playoffRounds`));
  const playedPlayoff = Object.values(existingRounds.val() || {}).reduce(
    (n: number, r: any) => n + countRecordedResults(r?.matches),
    0
  );
  // Byes are pre-completed, so only refuse when a real match has been played.
  const realPlayed = Object.values(existingRounds.val() || {}).reduce(
    (n: number, r: any) =>
      n +
      Object.values(r?.matches || {}).filter(
        (m: any) => m?.status === 'completed' && m?.player2
      ).length,
    0
  );
  if (realPlayed > 0) {
    throw new Error(
      `Refusing to regenerate the playoff bracket: ${realPlayed} playoff match(es) have already been played ` +
      'and would be erased. Use Repair advancement or Match Admin instead.'
    );
  }
  void playedPlayoff;

  const playoffRounds = buildPlayoffRounds(seeds, 'playoff_r');
  await set(ref(database, `tournaments/${tournamentId}/playoffRounds`), playoffRounds);
  await update(ref(database, `tournaments/${tournamentId}`), { status: 'playoffs' });
};

// ─── Live Scores ──────────────────────────────────────────────────────────────

export const updateLiveScores = async (
  tournamentId: string,
  matchId: string,
  scores: { [uid: string]: number | string }
): Promise<void> => {
  // `_hbAt` doubles as the heartbeat for the claim on this match: live scores
  // are already written while a game is being played, so liveness costs no
  // extra traffic. A claim with no recent _hbAt is an abandoned one.
  await update(ref(database, `tournaments/${tournamentId}/liveScores/${matchId}`), {
    ...scores,
    _hbAt: Date.now(),
  });
};

export const subscribeLiveScores = (
  tournamentId: string,
  callback: (scores: { [matchId: string]: { [uid: string]: number | string } }) => void
) => {
  const liveScoresRef = ref(database, `tournaments/${tournamentId}/liveScores`);
  return onValue(liveScoresRef, (snapshot) => {
    callback(snapshot.val() || {});
  });
};

// ─── Helper: find player's active/pending match ───────────────────────────────

export const getPlayerActiveMatch = (
  uid: string,
  tournament: Tournament
): TournamentMatch | null => {
  // Flatten every match across pool + playoffs
  const allMatches: TournamentMatch[] = [];
  if (tournament.poolMatches) allMatches.push(...Object.values(tournament.poolMatches));
  if (tournament.playoffRounds) {
    for (const round of Object.values(tournament.playoffRounds)) {
      if (round.matches) allMatches.push(...(Object.values(round.matches) as TournamentMatch[]));
    }
  }

  const involves = (m: TournamentMatch, u: string) =>
    m.player1?.uid === u || m.player2?.uid === u;

  // Players currently tied up in an active match elsewhere
  const busy = new Set<string>();
  allMatches.forEach((m) => {
    if (m.status === 'active') {
      if (m.player1?.uid) busy.add(m.player1.uid);
      if (m.player2?.uid) busy.add(m.player2.uid);
    }
  });

  // 1. If the player is already in an active match, that's their match (resume it).
  const active = allMatches.find((m) => m.status === 'active' && involves(m, uid));
  if (active) return active;

  // 2. Prefer a pending match the player can actually start NOW — i.e. one whose
  //    opponent isn't busy in another match. This avoids telling someone to wait
  //    on a busy opponent when a different, playable opponent is free.
  //    A match is only startable once BOTH slots are filled: a bracket match can
  //    legitimately be pending with just one player while it waits for a feeder
  //    match's winner (bye advanced into a slot, or an odd-index winner landed in
  //    player2 before player1's feeder finished). Those must be skipped, not
  //    treated as "your match" — reading the missing slot used to crash the page.
  const myPending = allMatches.filter(
    (m) => m.status === 'pending' && involves(m, uid) && !!m.player1 && !!m.player2
  );
  const playableNow = myPending.find((m) => {
    const opp = m.player1?.uid === uid ? m.player2?.uid : m.player1?.uid;
    return opp && !busy.has(opp);
  });
  if (playableNow) return playableNow;

  // 3. Fall back to any pending match (the UI shows the "opponent busy" wait state).
  return myPending[0] || null;
};

// ─── Championship ─────────────────────────────────────────────────────────────

/**
 * Crown a new champion: clears the previous holder's flag, sets the new one,
 * and writes a global /currentChampion entry.
 */
export const crownChampion = async (
  tournamentId: string,
  tournamentName: string,
  winnerUid: string,
  winnerName: string
): Promise<void> => {
  const wonAt = Date.now();

  // Read current global champion to clear their flag
  const currentChampSnap = await get(ref(database, 'currentChampion'));
  const updates: { [path: string]: any } = {};

  if (currentChampSnap.exists()) {
    const prev = currentChampSnap.val();
    if (prev.uid && prev.uid !== winnerUid) {
      updates[`users/${prev.uid}/isCurrentChampion`] = false;
      updates[`users/${prev.uid}/championTitle`] = null;
    }
  }

  // Set new champion
  updates['currentChampion'] = { uid: winnerUid, name: winnerName, tournamentId, tournamentName, wonAt };
  updates[`users/${winnerUid}/isCurrentChampion`] = true;
  updates[`users/${winnerUid}/championTitle`] = { tournamentId, tournamentName, wonAt };

  await update(ref(database), updates);
};

export const subscribeCurrentChampion = (
  callback: (champ: { uid: string; name: string; tournamentId: string; tournamentName: string; wonAt: number } | null) => void
) => {
  return onValue(ref(database, 'currentChampion'), (snap) => {
    callback(snap.exists() ? snap.val() : null);
  });
};

// ─── CSV Export ───────────────────────────────────────────────────────────────

/**
 * Build a CSV string from tournament results.
 * Returns the CSV content as a string — caller triggers the download.
 */
export const buildTournamentCSV = (tournament: Tournament): string => {
  const rows: string[][] = [];

  if (tournament.format === 'pool' && tournament.groups && tournament.poolMatches) {
    // Header
    rows.push([
      'Player', 'Group', 'Played', 'Wins', 'Losses', 'Round +/-', 'Points',
      'Triplets', 'Freezes', 'Triples Avg', 'Accuracy %', 'Points/round', 'Advanced',
    ]);

    Object.values(tournament.groups)
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((group) => {
        const standings = computeGroupStandings(
          group.id,
          group.playerIds,
          tournament.poolMatches || {},
          tournament.participants
        );
        standings.forEach((s, idx) => {
          const advanced = idx < (tournament.poolSettings?.advanceCount || 2) ? 'Yes' : 'No';
          rows.push([
            s.name,
            group.name,
            String(s.played),
            String(s.wins),
            String(s.losses),
            String(s.roundDiff),
            String(s.points),
            String(s.triplesTotal),
            String(s.freezes),
            s.triplesAvg.toFixed(2),
            s.accuracy.toFixed(1),
            s.pointsPerRound.toFixed(2),
            advanced,
          ]);
        });
      });

    // Playoff results if present
    if (tournament.playoffRounds) {
      rows.push([]);
      rows.push(['Playoffs']);
      rows.push(['Round', 'Player 1', 'Player 2', 'Score', 'Winner']);
      Object.values(tournament.playoffRounds)
        .sort((a, b) => a.roundIndex - b.roundIndex)
        .forEach((round) => {
          Object.values(round.matches || {}).forEach((m: any) => {
            if (m.status === 'completed' && m.result) {
              rows.push([
                round.name,
                m.player1?.name || '',
                m.player2?.name || '',
                `${m.result.player1Wins}-${m.result.player2Wins}`,
                tournament.participants?.[m.result.winner]?.name || m.result.winner,
              ]);
            }
          });
        });
    }
  } else if (tournament.format === 'bracket' && tournament.playoffRounds) {
    rows.push(['Round', 'Player 1', 'Player 2', 'Score', 'Winner']);
    Object.values(tournament.playoffRounds)
      .sort((a, b) => a.roundIndex - b.roundIndex)
      .forEach((round) => {
        Object.values(round.matches || {}).forEach((m: any) => {
          if (m.status === 'completed' && m.result) {
            rows.push([
              round.name,
              m.player1?.name || '',
              m.player2?.name || 'BYE',
              `${m.result.player1Wins}-${m.result.player2Wins}`,
              tournament.participants?.[m.result.winner]?.name || m.result.winner,
            ]);
          }
        });
      });
  }

  if (tournament.winner) {
    rows.push([]);
    rows.push([`Tournament Winner: ${tournament.winner.name}`]);
  }

  return rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
};

/** Build ordered participant list for bracket start (respects manual seeds / random) */
export const buildBracketSeedOrder = (
  participants: { [uid: string]: TournamentParticipant },
  seedingType: 'random' | 'manual'
): { uid: string; name: string }[] => {
  const approved = Object.values(participants).filter((p) => p.status === 'approved');

  if (seedingType === 'manual') {
    return approved
      .sort((a, b) => (a.seed ?? 999) - (b.seed ?? 999))
      .map((p) => ({ uid: p.uid, name: p.name }));
  }

  return shuffle(approved).map((p) => ({ uid: p.uid, name: p.name }));
};
